import React, { useRef, useEffect, ComponentType } from 'react';
// Declare process for browser environment
declare const process: {
  env: {
    NODE_ENV: string;
  };
};

import { getMemoryLeakConfig, shouldMonitorComponent } from '@/utils/memoryLeakConfig';
import { RenderTracker, createCleanupTracker } from '@/utils/memoryLeakUtils';

// Helper to detect if we're in a hot reload cycle
function isHotReloading(): boolean {
  // Check if we have React refresh context or hot reloading indicators
  if (typeof window !== 'undefined') {
    // Look for React refresh indicators in the call stack
    const stack = new Error().stack ?? '';
    return (
      stack.includes('@react-refresh') ||
      stack.includes('performReactRefresh') ||
      stack.includes('validateRefreshBoundaryAndEnqueueUpdate') ||
      // Also check for Vite HMR indicators
      (window as unknown as { __vite_plugin_react_preamble_installed__?: boolean })
        .__vite_plugin_react_preamble_installed__ === true
    );
  }
  return false;
}

interface MemoryLeakMonitorProps {
  componentName?: string;
  enableRenderTracking?: boolean;
  enableLeakDetection?: boolean;
  warnOnExcessiveRenders?: boolean;
  warnOnLongMountTime?: boolean;
  renderThreshold?: number;
  frequencyThreshold?: number;
  mountTimeThreshold?: number;
}

// Helper function to resolve monitor configuration options
function resolveMonitorOptions(options: MemoryLeakMonitorProps, componentName: string) {
  const globalConfig = getMemoryLeakConfig();
  const shouldMonitor = shouldMonitorComponent(componentName);

  // If component is explicitly excluded from monitoring, disable all monitoring
  // regardless of explicit options
  if (!shouldMonitor) {
    return {
      enableRenderTracking: false,
      enableLeakDetection: false,
      warnOnExcessiveRenders: false,
      warnOnLongMountTime: false,
      renderThreshold: globalConfig.renderThreshold,
      frequencyThreshold: globalConfig.frequencyThreshold,
      mountTimeThreshold: globalConfig.mountTimeThreshold,
    };
  }

  return {
    enableRenderTracking: options.enableRenderTracking ?? globalConfig.enableRenderTracking,
    enableLeakDetection: options.enableLeakDetection ?? globalConfig.enableLeakDetection,
    warnOnExcessiveRenders: options.warnOnExcessiveRenders ?? globalConfig.warnOnExcessiveRenders,
    warnOnLongMountTime: options.warnOnLongMountTime ?? globalConfig.warnOnLongMountTime,
    renderThreshold: options.renderThreshold ?? globalConfig.renderThreshold,
    frequencyThreshold: options.frequencyThreshold ?? globalConfig.frequencyThreshold,
    mountTimeThreshold: options.mountTimeThreshold ?? globalConfig.mountTimeThreshold,
  };
}

/**
 * Higher-Order Component that monitors components for memory leaks and performance issues.
 * Only active in development mode.
 *
 * @param WrappedComponent - The component to wrap
 * @param options - Configuration options
 * @returns Wrapped component with memory leak monitoring
 *
 * @example
 * ```tsx
 * const MonitoredMyComponent = withMemoryLeakMonitor(MyComponent, {
 *   componentName: 'MyComponent',
 *   enableRenderTracking: true,
 *   warnOnExcessiveRenders: true,
 * });
 * ```
 */
export function withMemoryLeakMonitor<P extends object>(
  WrappedComponent: ComponentType<P>,
  options: MemoryLeakMonitorProps = {}
) {
  const componentName = options.componentName ?? 'UnknownComponent';
  const {
    enableRenderTracking,
    enableLeakDetection,
    warnOnExcessiveRenders,
    warnOnLongMountTime,
    renderThreshold,
    frequencyThreshold,
    mountTimeThreshold,
  } = resolveMonitorOptions(options, componentName);

  function MonitoredComponent(props: P) {
    const renderTrackerRef = useRef<RenderTracker>();
    const cleanupTrackerRef = useRef<ReturnType<typeof createCleanupTracker>>();
    const mountTimeRef = useRef<number>();
    const renderCountRef = useRef(0);
    const isMountedRef = useRef(true);

    // Initialize trackers
    useEffect(() => {
      if (process.env.NODE_ENV === 'development') {
        if (enableRenderTracking) {
          renderTrackerRef.current = new RenderTracker(componentName);
        }

        if (enableLeakDetection) {
          cleanupTrackerRef.current = createCleanupTracker(componentName);
        }

        mountTimeRef.current = Date.now();
      }

      return () => {
        isMountedRef.current = false;

        if (process.env.NODE_ENV === 'development') {
          const mountDuration = Date.now() - (mountTimeRef.current ?? 0);

          if (enableLeakDetection && cleanupTrackerRef.current) {
            cleanupTrackerRef.current.warnOnUnmount();
          }

          if (enableRenderTracking && renderTrackerRef.current) {
            const stats = renderTrackerRef.current.getStats();

            // Check for long mount time
            const shouldWarnLongMount = warnOnLongMountTime && mountDuration > mountTimeThreshold;

            // Only log if there are actual issues (warnings), not for normal operation
            if (shouldWarnLongMount) {
              console.warn(
                `[LONG MOUNT TIME] [MemoryLeakMonitor] ${componentName} unmounted after ${mountDuration}ms ` +
                  `with ${stats.renderCount} renders (${stats.rendersPerSecond.toFixed(1)} renders/sec) ` +
                  `(threshold: ${mountTimeThreshold}ms)`
              );
            }
          }
        }
      };
    }, []);

    // Track renders
    useEffect(() => {
      if (
        process.env.NODE_ENV === 'development' &&
        enableRenderTracking &&
        renderTrackerRef.current
      ) {
        renderTrackerRef.current.trackRender();
      }

      renderCountRef.current++;
    });

    // Development-only warnings for excessive renders
    useEffect(() => {
      if (
        process.env.NODE_ENV === 'development' &&
        warnOnExcessiveRenders &&
        renderCountRef.current >= renderThreshold
      ) {
        const timeSinceMount = Date.now() - (mountTimeRef.current ?? 0);
        const rendersPerSecond = (renderCountRef.current / timeSinceMount) * 1000;

        if (rendersPerSecond > frequencyThreshold) {
          console.warn(
            `[MemoryLeakMonitor] ${componentName}: Excessive renders detected ` +
              `(${renderCountRef.current} renders in ${timeSinceMount}ms = ${rendersPerSecond.toFixed(1)} renders/sec). ` +
              'Consider optimizing with React.memo, useMemo, useCallback, or reducing prop drilling.'
          );
        }
      }
    }, []);

    // Track effect setups (when used with useMemoryLeakDetector)

    // Only render the wrapped component if mounted
    if (!isMountedRef.current && process.env.NODE_ENV === 'development') {
      // Skip warning during hot reloading as this is expected behavior
      if (!isHotReloading()) {
        console.warn(
          `[MemoryLeakMonitor] ${componentName}: Attempted to render after unmount. ` +
            'This should not happen and may indicate improper state management.'
        );
      }
      return null;
    }

    return <WrappedComponent {...props} />;
  }

  MonitoredComponent.displayName = `withMemoryLeakMonitor(${componentName})`;

  return MonitoredComponent;
}

/**
 * Hook version of memory leak monitor for functional components
 * that prefer hooks over HOCs
 */
export function useMemoryLeakMonitor(options: MemoryLeakMonitorProps = {}) {
  const componentName = options.componentName ?? 'UnknownComponent';
  const {
    enableRenderTracking,
    enableLeakDetection,
    warnOnExcessiveRenders,
    renderThreshold,
    frequencyThreshold,
  } = resolveMonitorOptions(options, componentName);

  const renderTrackerRef = useRef<RenderTracker>();
  const cleanupTrackerRef = useRef<ReturnType<typeof createCleanupTracker>>();
  const mountTimeRef = useRef<number>();
  const renderCountRef = useRef(0);

  // Initialize trackers
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      if (enableRenderTracking) {
        renderTrackerRef.current = new RenderTracker(componentName);
      }

      if (enableLeakDetection) {
        cleanupTrackerRef.current = createCleanupTracker(componentName);
      }

      mountTimeRef.current = Date.now();
    }

    return () => {
      if (process.env.NODE_ENV === 'development') {
        const mountDuration = Date.now() - (mountTimeRef.current ?? 0);

        if (enableLeakDetection && cleanupTrackerRef.current) {
          cleanupTrackerRef.current.warnOnUnmount();
        }

        if (enableRenderTracking && renderTrackerRef.current) {
          const stats = renderTrackerRef.current.getStats();

          console.log(
            `[MemoryLeakMonitor] ${componentName} unmounted after ${mountDuration}ms ` +
              `with ${stats.renderCount} renders (${stats.rendersPerSecond.toFixed(1)} renders/sec)`
          );
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track renders
  useEffect(() => {
    if (
      process.env.NODE_ENV === 'development' &&
      enableRenderTracking &&
      renderTrackerRef.current
    ) {
      renderTrackerRef.current.trackRender();
    }

    renderCountRef.current++;
  });

  // Development-only warnings
  useEffect(() => {
    if (
      process.env.NODE_ENV === 'development' &&
      warnOnExcessiveRenders &&
      renderCountRef.current >= renderThreshold
    ) {
      const timeSinceMount = Date.now() - (mountTimeRef.current ?? 0);
      const rendersPerSecond = (renderCountRef.current / timeSinceMount) * 1000;

      if (rendersPerSecond > frequencyThreshold) {
        console.warn(
          `[MemoryLeakMonitor] ${componentName}: Excessive renders detected ` +
            `(${renderCountRef.current} renders in ${timeSinceMount}ms = ${rendersPerSecond.toFixed(1)} renders/sec). ` +
            'Consider optimizing with React.memo, useMemo, useCallback, or reducing prop drilling.'
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    trackEffectSetup: (description = 'effect') => {
      if (
        process.env.NODE_ENV === 'development' &&
        enableLeakDetection &&
        cleanupTrackerRef.current
      ) {
        cleanupTrackerRef.current.trackSetup(description);
      }
    },

    trackEffectCleanup: (description = 'effect') => {
      if (
        process.env.NODE_ENV === 'development' &&
        enableLeakDetection &&
        cleanupTrackerRef.current
      ) {
        cleanupTrackerRef.current.trackCleanup(description);
      }
    },
  };
}
